import { useState } from "react";
import { AVATARS, PLAYER_COLORS } from "@shared/palette";
import { asset } from "../lib/asset";

export type Mood = "idle" | "happy" | "sad" | "pick" | "talk" | "still";

interface Props {
  index: number;
  color?: number;
  size?: number;
  mood?: Mood;
  /** draw the coloured disc behind the sticker */
  disc?: boolean;
  dim?: boolean;
  className?: string;
  title?: string;
}

const failed = new Set<string>();

/** A player's 3D sticker. Happy and sad moods swap the artwork and add sparkles or a rain cloud. */
export function Avatar({ index, color, size = 56, mood = "still", disc = true, dim, className = "", title }: Props) {
  const a = AVATARS[index] ?? AVATARS[0];
  const variant = mood === "happy" ? "-happy" : mood === "sad" ? "-sad" : "";
  const src = asset(`img/avatars/${a.id}${variant}.webp`);
  const base = asset(`img/avatars/${a.id}.webp`);
  const [, force] = useState(0);
  const useSrc = failed.has(src) ? (failed.has(base) ? null : base) : src;
  const ring = color !== undefined ? PLAYER_COLORS[color % PLAYER_COLORS.length] : "#5a2ea6";
  const anim = mood === "idle" ? "av-idle" : mood === "happy" ? "av-happy" : mood === "sad" ? "av-sad" : mood === "pick" ? "av-pick" : mood === "talk" ? "av-talk" : "";
  return (
    <span
      className={`relative inline-grid shrink-0 place-items-center ${className}`}
      style={{ width: size, height: size, opacity: dim ? 0.45 : 1 }}
      title={title}
    >
      {disc && (
        <span
          aria-hidden
          className="absolute inset-0 rounded-full"
          style={{ background: `color-mix(in oklab, ${ring} 28%, #2a1350)`, boxShadow: `inset 0 0 0 ${Math.max(2, size / 22)}px ${ring}` }}
        />
      )}
      <span className={`relative grid h-full w-full place-items-center ${anim}`}>
        {useSrc ? (
          <img
            src={useSrc}
            alt=""
            draggable={false}
            className="pointer-events-none h-[112%] w-[112%] max-w-none object-contain"
            style={{ marginTop: disc ? "-8%" : 0 }}
            onError={() => {
              failed.add(useSrc);
              force((n) => n + 1);
            }}
          />
        ) : (
          <span aria-hidden style={{ fontSize: size * 0.56, lineHeight: 1 }}>
            {a.emoji}
          </span>
        )}
      </span>
      {mood === "happy" && size >= 44 && <Sparkles size={size} />}
      {mood === "sad" && size >= 44 && <RainCloud size={size} />}
    </span>
  );
}

function Sparkles({ size }: { size: number }) {
  const spots = [
    { x: -6, y: 8, s: 0.26, d: 0 },
    { x: 84, y: 0, s: 0.2, d: 0.35 },
    { x: 90, y: 62, s: 0.16, d: 0.7 },
  ];
  return (
    <span aria-hidden className="pointer-events-none absolute inset-0">
      {spots.map((p, i) => (
        <svg
          key={i}
          viewBox="0 0 20 20"
          className="spark absolute"
          style={{ left: `${p.x}%`, top: `${p.y}%`, width: size * p.s, height: size * p.s, animationDelay: `${p.d}s` }}
        >
          <path d="M10 0c.9 5.2 4.8 9.1 10 10-5.2.9-9.1 4.8-10 10-.9-5.2-4.8-9.1-10-10C5.2 9.1 9.1 5.2 10 0z" fill="#FFD23F" />
        </svg>
      ))}
    </span>
  );
}

function RainCloud({ size }: { size: number }) {
  const w = size * 0.62;
  return (
    <span aria-hidden className="pointer-events-none absolute" style={{ left: "50%", top: -size * 0.3, width: w, marginLeft: -w / 2 }}>
      <svg viewBox="0 0 60 40" width={w} height={w * 0.66}>
        <path d="M16 30h30a11 11 0 0 0 0-22 15 15 0 0 0-28 4A9 9 0 0 0 16 30z" fill="#9C8BC4" />
        {[18, 30, 42].map((x, i) => (
          <rect key={x} className="rain-drop" style={{ animationDelay: `${i * 0.3}s` }} x={x} y={30} width={3} height={7} rx={1.5} fill="#7FD3FF" />
        ))}
      </svg>
    </span>
  );
}

export function avatarName(index: number): string {
  return AVATARS[index]?.id ?? "khinkali";
}
